import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import type { DBStoragePricingTier } from "@/lib/db-types";

/**
 * VIB-1308: konfigurerbare trinnpriser for lagringsfakturering. Satsene
 * (grenser/pris/prismodell) er blokkert på Sølvrev-avklaring B1–B3 og seedes
 * ikke i kode — admin registrerer dem her når de foreligger. Tom liste →
 * ingen storage-fakturering.
 */
export function useStoragePricingTiers() {
  return useQuery({
    queryKey: ["storage-pricing-tiers"],
    queryFn: async (): Promise<DBStoragePricingTier[]> => {
      const { data, error } = await supabase
        .from("storage_pricing_tiers")
        .select("*")
        .is("deleted_at", null)
        .order("valid_from", { ascending: false })
        .order("min_gb", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DBStoragePricingTier[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface StorageTierInput {
  min_gb: number;
  max_gb: number | null;
  price: number;
  price_model: "flat" | "per_gb";
  valid_from: string;
}

/** Admin-mutations for trinnpris-tabellen. RLS begrenser skriving til admin. */
export function useStoragePricingTierMutations() {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();

  const invalidate = () => invalidateFor(qc, "storage_pricing_tiers");

  const createTier = useMutation({
    mutationFn: async (input: StorageTierInput): Promise<DBStoragePricingTier> => {
      const { data, error } = await supabase
        .from("storage_pricing_tiers")
        .insert({ ...input, created_by: authUser?.id ?? null })
        .select()
        .single();
      if (error) throw error;
      return data as DBStoragePricingTier;
    },
    onSuccess: async () => {
      await invalidate();
      toast.success("Pristrinn lagt til");
    },
    onError: (err: Error) => toast.error(`Kunne ikke lagre pristrinn: ${err.message}`),
  });

  const softDeleteTier = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from("storage_pricing_tiers")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidate();
      toast.success("Pristrinn fjernet");
    },
    onError: (err: Error) => toast.error(`Kunne ikke fjerne pristrinn: ${err.message}`),
  });

  return { createTier, softDeleteTier };
}
